import React, { SyntheticEvent, useRef } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Container from '@grnhse/seedling/lib/birch/components/container';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { withLocalePrefix } from 'shared/utils/translation';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import StyledTag from 'talent_matching/components/shared/styled_tag';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { IconTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import capitalize from 'lodash/capitalize';
import Popover from '@mui/material/Popover';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { MatchedSkill } from 'talent_matching/types';
import {
  matchCategoryColor,
  matchCategoryRank,
  CategoryValue,
} from 'talent_matching/components/shared/match_category';
import Divider from '@grnhse/seedling/lib/birch/components/divider';

const t = withLocalePrefix('talent_matching');
const tImportance = withLocalePrefix(
  'talent_matching.modals.calibration.importance'
);

const importanceLabelMap: Record<number, string> = {
  1: tImportance('not_very_important'),
  2: tImportance('somewhat_important'),
  3: tImportance('important'),
  4: tImportance('very_important'),
  5: tImportance('crucial'),
};

type SkillGroup = {
  baseSkill: string;
  importance: number;
  terms: MatchedSkill[];
};

type SkillSection = {
  key: string;
  skills: MatchedSkill[];
};

const SECTION_ORDER = ['technical', 'baseline'];

const groupSkillsBySection = (skills: MatchedSkill[]): SkillSection[] => {
  const sectionMap = new Map<string, MatchedSkill[]>();

  skills
    .filter((skill) => skill.name)
    .forEach((skill) => {
      const key =
        skill.section && SECTION_ORDER.includes(skill.section)
          ? skill.section
          : 'other';
      const existing = sectionMap.get(key);
      if (existing) {
        existing.push(skill);
      } else {
        sectionMap.set(key, [skill]);
      }
    });

  const sectionOrderIndex = (key: string) => {
    const index = SECTION_ORDER.indexOf(key);
    return index === -1 ? SECTION_ORDER.length : index;
  };

  return Array.from(sectionMap.entries())
    .sort(([a], [b]) => sectionOrderIndex(a) - sectionOrderIndex(b))
    .map(([key, sectionSkills]) => ({
      key,
      skills: [...sectionSkills].sort(
        (a, b) =>
          matchCategoryRank(a.assessmentCategory) -
            matchCategoryRank(b.assessmentCategory) ||
          (a.rank ?? Infinity) - (b.rank ?? Infinity) ||
          (a.name ?? '').localeCompare(b.name ?? '')
      ),
    }));
};

type Props = {
  skills: MatchedSkill[];
  numCalibrationSkills: number;
  openCalibrationModal?: () => void;
  showCountBoxes?: boolean;
  buttonSize?: 'md' | 'sm';
};

const groupSkillsByBaseSkill = (skills: MatchedSkill[]): SkillGroup[] => {
  const groupMap = new Map<string, SkillGroup>();

  skills.forEach((skill) => {
    const key = skill.base_skill.toLowerCase();
    const existing = groupMap.get(key);
    if (existing) {
      existing.terms.push(skill);
    } else {
      groupMap.set(key, {
        baseSkill: skill.base_skill,
        importance: skill.importance,
        terms: [skill],
      });
    }
  });

  return Array.from(groupMap.values()).sort(
    (a, b) => b.importance - a.importance
  );
};

const MatchingSkills = ({
  skills,
  numCalibrationSkills,
  openCalibrationModal,
  showCountBoxes = true,
  buttonSize = 'md',
}: Props) => {
  const [popperIsOpen, openPopper, closePopper] = useModal();
  const anchorEl = useRef<HTMLDivElement | null>(null);

  const onClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    openPopper();
  };

  if (skills.length === 0) {
    return null;
  }

  const isCategorized = skills.some(
    (skill) => skill.section || skill.assessmentCategory
  );
  const groups = isCategorized ? [] : groupSkillsByBaseSkill(skills);
  const sections = isCategorized ? groupSkillsBySection(skills) : [];
  const numMatchedCalibrationSkills = groups.length;

  const onOpenCalibrationModal = () => {
    closePopper();
    openCalibrationModal?.();
  };

  return (
    <Stack ref={anchorEl}>
      <Grid sx={{ marginLeft: '-6px' }}>
        <TertiaryButton
          onClick={onClick}
          size={buttonSize}
          icon={ArrowDownIcon}
          placement="right"
        >
          {t('view_matched_skills')}
        </TertiaryButton>
      </Grid>

      <Popover
        open={popperIsOpen}
        onClose={closePopper}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack
          sx={{
            padding: `${Tokens.spacing['2']}`,
            width: '460px',
          }}
        >
          <Stack sx={{ padding: 0, minWidth: '300px' }} spacing={2}>
            <Stack direction="column" spacing={0.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Body medium>{t('matched_skills_title')}</Body>
                <IconButton
                  icon={CloseIcon}
                  onClick={closePopper}
                  label={t('close')}
                  size="sm"
                  flat
                />
              </Stack>

              {numCalibrationSkills > 0 && (
                <SecondaryBody metadata>
                  {t('matched_skills_subtitle')}
                </SecondaryBody>
              )}
            </Stack>

            {showCountBoxes &&
              (numCalibrationSkills > 0 ? (
                <Stack direction="row" spacing={1}>
                  <Container>
                    <SecondaryBody metadata>
                      {t('calibrated_skills_matched')}
                    </SecondaryBody>
                    <Body medium>
                      {t('matched_of_total', {
                        matched: numMatchedCalibrationSkills,
                        total: numCalibrationSkills,
                      })}
                    </Body>
                  </Container>
                  <Container>
                    <SecondaryBody metadata>
                      {t('matched_terms_on_resume')}
                    </SecondaryBody>
                    <Body medium>{skills.length}</Body>
                  </Container>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Container>
                    <SecondaryBody metadata>
                      {t('matched_skills_title')}
                    </SecondaryBody>
                    <Body medium>{skills.length}</Body>
                  </Container>
                </Stack>
              ))}

            {isCategorized
              ? sections.map((section) => (
                  <Stack key={section.key} spacing={1}>
                    <Body medium>
                      {t(`matched_skills_sections.${section.key}`)}
                    </Body>
                    {section.skills.map((skill) => (
                      // Skill name and its AdeptID category on one line.
                      <Stack
                        key={skill.name ?? ''}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        spacing={1}
                      >
                        <SecondaryBody>
                          {capitalize(skill.name ?? '')}
                        </SecondaryBody>
                        {skill.assessmentCategory && (
                          <CategoryValue
                            $color={matchCategoryColor(
                              skill.assessmentCategory
                            )}
                          >
                            {skill.assessmentCategory}
                          </CategoryValue>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                ))
              : groups.map((group) => (
                  <Stack key={group.baseSkill} spacing={1}>
                    <Stack direction="row" spacing={1}>
                      <Body medium>{capitalize(group.baseSkill)}</Body>
                      <Body>{'-'}</Body>
                      {importanceLabelMap[group.importance] && (
                        <Body>{importanceLabelMap[group.importance]}</Body>
                      )}
                    </Stack>
                    <Stack
                      spacing={1}
                      direction="row"
                      flexWrap="nowrap"
                      alignContent="flex-start"
                      sx={{ padding: 0 }}
                    >
                      <Stack direction="row" sx={{ whiteSpace: 'nowrap' }}>
                        <SecondaryBody metadata>
                          {t('terms_found')}
                        </SecondaryBody>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {group.terms.map((skill) => (
                          // using IconTooltip instead of Tooltip to match the preferred design
                          <IconTooltip
                            key={skill.name ?? ''}
                            label={skill.exact ? t('exact') : t('similar')}
                            ariaLabel={skill.name ?? ''}
                            icon={() => (
                              <StyledTag
                                bgColor={
                                  skill.exact
                                    ? FullPalette.YELLOW_E25
                                    : FullPalette.VIOLET_E50
                                }
                                text={capitalize(skill.name ?? '')}
                              />
                            )}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Stack>
                ))}

            <Divider />

            {openCalibrationModal && (
              <Stack direction="row" justifyContent="flex-end">
                <TertiaryButton onClick={onOpenCalibrationModal} size="md">
                  {t('edit_calibration')}
                </TertiaryButton>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};

export default MatchingSkills;
